/**
 * The mark, as geometry alone — no React, no DOM, no JSX.
 *
 * ITS OWN MODULE SO A RENDERER THAT IS NOT A BROWSER CAN HAVE IT. The PDF documents draw this
 * outline with jsPDF's `moveTo`/`curveTo`/`fill`, and those documents are now built in two
 * places: in the operator's browser when they press Export, and in the worker when a statement is
 * mailed with nobody present. The worker is a Node process with no React in it and no business
 * acquiring one, so the path cannot live behind a component import.
 *
 * `Logo.tsx` imports from HERE rather than the other way round, which keeps the rule the mark
 * has always carried: one copy of the outline, regenerated rather than hand-edited, and every
 * renderer scaling the same numbers.
 */

/**
 * The drawing's own bounding box, inside the 6000×4000 canvas it was exported on.
 *
 * The export leaves most of that canvas empty. A `viewBox` of `0 0 6000 4000` therefore scales
 * the MARGIN to fit the tile and leaves the mark occupying a fraction of it, which is why an
 * untrimmed export reads as small and thin at every size. Windowing the viewBox onto the ink is
 * the whole fix.
 *
 * MEASURED, not eyeballed: these come from solving each cubic's derivative for its extrema, so
 * the box is tight against the curves themselves rather than against their control points —
 * which on this mark's arc would have left visible slack at the top.
 */
export const INK = { x: 1780, y: 354.319, width: 2302.836, height: 3265.475 } as const;
export const VIEW_BOX = `${INK.x} ${INK.y} ${INK.width} ${INK.height}`;

/**
 * The mark is TALLER than it is wide (0.705 : 1). Forcing `width === height` letterboxes it
 * inside its own box, so callers compensate by oversizing the svg past the tile containing it.
 * Sizing by height and deriving width means `size` is the number you actually see.
 */
export const ASPECT = INK.width / INK.height;

/**
 * The whole mark, as ONE path.
 *
 * It arrived as a body path plus a separate `<circle>` for the head, inside two nested
 * transforms — an outer translate over both, and a scale-plus-translate around the circle. Both
 * are baked in here. Every transform was scale plus translate with a positive determinant, so no
 * contour's winding flipped, which is the thing that would otherwise punch holes where shapes
 * overlap. The circle became four cubic arcs at the usual 0.5523 constant.
 *
 * ONE PATH RATHER THAN TWO ELEMENTS, for the reason that has always applied to this mark:
 * neighbouring shapes that share an edge without overlapping are composited one after the other,
 * so a pixel straddling the join takes partial coverage from each — 50% over 50% is 75%, not
 * 100%. That missing 25% is a hairline seam, and it is a compositing artifact rather than a
 * resolution one, so exporting a PNG instead would only bake the same seams in at a fixed size.
 *
 * FILL RULE: the export declares `evenodd` and this renders `nonzero`. That is checked rather
 * than lucky — no contour in this artwork encloses another, the head sits in space the body does
 * not fill, and both rules were rendered and compared before the swap. `nonzero` is kept so this
 * and favicon.svg agree, and so any subpath added later unions rather than punching a hole.
 *
 * REGENERATE, do not hand-edit, if the artwork changes: parse the export, compose the nested
 * matrices, convert primitives to cubics, re-measure the box. Editing these numbers by hand is
 * how a mark ends up fractionally off its own centre with nothing to compare against.
 */
export const MARK =
  'M2100.621,1469.319L2120.621,1449.319L2120.621,3619.319L1780,3619.319L1780,1449.319' +
  'C1779.999,844.567 2270.248,354.319 2875,354.319C3479.346,354.319 3970,844.972 3970,1449.319' +
  'C3970,1649.54 3915.134,1844.782 3812.897,2014.455' +
  'C3981.913,2158.995 4078.625,2369.523 4081.858,2584.43' +
  'C4082.935,2656.024 4082.834,2656.014 4082.833,3591.898' +
  'C4082.833,3601.522 4080.033,3607.023 4077.336,3610.535' +
  'C4073.658,3615.324 4068.916,3618.298 4062.542,3619.496' +
  'C4059.139,3620.135 4054.021,3619.547 4047.051,3619.547' +
  'C3819.811,3619.549 3774.177,3619.588 3764.546,3619.11' +
  'C3754.765,3618.624 3752.724,3616.031 3751.654,3615.301L3748.138,3612.269L3744.381,3606.52' +
  'C3744.012,3605.669 3742.938,3603.929 3742.622,3598.626' +
  'C3742.051,3589.032 3742.041,3499.539 3742.048,2606.986' +
  'C3742.048,2600.636 3750.176,2520.506 3702.718,2416.925' +
  'C3675.468,2357.451 3624.369,2303.559 3602.003,2281.644' +
  'C3575.444,2321.604 3517.074,2425.79 3498.675,2531.717' +
  'C3485.654,2606.679 3486.454,2629.648 3486.89,2642.619' +
  'C3487.148,2650.295 3487.117,2655.422 3485.544,2662.581' +
  'C3485.118,2664.516 3483.916,2666.891 3482.687,2668.966' +
  'C3482.481,2735.473 3483.108,3430.418 3482.498,3572.921' +
  'C3482.433,3588.055 3482.337,3597.185 3482.243,3598.999L3481.46,3603.815L3479.618,3608.182' +
  'L3474.438,3614.22L3469.338,3617.175C3468.277,3617.605 3466.804,3618.126 3464.602,3618.511' +
  'C3461.035,3619.134 3444.914,3619.626 3365.31,3619.536L3175.189,3619.547' +
  'C3175.313,3619.547 3176.54,3619.466 3177.24,3619.418' +
  'C3174.784,3619.572 3163.544,3620.108 3157.364,3617.561' +
  'C3148.161,3613.767 3142.36,3606.631 3142.167,3595.667' +
  'C3141.966,3584.249 3143.079,2669.411 3142.337,2666.157' +
  'C3140.801,2659.423 3133.428,2485.666 3213.088,2289.391' +
  'C3234.517,2236.592 3256.275,2194.968 3270.414,2169.868' +
  'C3214.14,2175.391 3096.853,2201.933 3004.831,2297.362' +
  'C3003.013,2299.247 2883.968,2408.485 2881.12,2593.577' +
  'C2881.068,2596.981 2880.905,2596.62 2880.905,2635.819' +
  'C2880.905,3502.037 2882.545,3589.097 2882.07,3598.627' +
  'C2881.778,3604.475 2880.45,3606.449 2879.94,3607.501L2875.746,3613.283L2871.91,3616.207' +
  'C2870.992,3616.748 2869.097,3618.871 2860.174,3619.23' +
  'C2851.347,3619.584 2809.289,3619.547 2599.658,3619.547' +
  'C2581.824,3619.547 2571.878,3619.769 2566.522,3619.38' +
  'C2560.138,3618.916 2556.274,3617.367 2553.859,3616.028' +
  'C2552.295,3615.16 2550.97,3614.192 2549.824,3613.174L2546.031,3608.769L2543.54,3603.512' +
  'L2542.58,3598.713C2542.523,3598.068 2542.453,3598.175 2542.453,2620.689' +
  'C2542.453,2512.595 2550.144,2463.972 2576.898,2370.224' +
  'C2578.575,2364.346 2610.255,2257.831 2681.696,2157.401' +
  'C2880.84,1877.451 3213.94,1776.112 3510.807,1855.438' +
  'C3587.915,1734.723 3629.441,1593.905 3629.441,1449.319' +
  'C3629.441,1032.931 3291.387,694.877 2875,694.877' +
  'C2458.333,694.877 2120.558,1032.652 2120.558,1449.319L2100.621,1469.256L2100.621,1469.319Z' +
  'M2100.58,1449.319L2100.587,1442.548C2100.568,1444.803 2100.558,1447.06 2100.558,1449.319' +
  'L2100.58,1449.319ZM3480.22,2672.774C3479.719,2673.489 3479.395,2673.92 3479.395,2673.92' +
  'C3479.395,2673.92 3479.746,2673.501 3480.22,2672.774ZM3196.798,1386.818' +
  'C3196.798,1564.543 3052.724,1708.616 2875,1708.616' +
  'C2697.276,1708.616 2553.202,1564.543 2553.202,1386.818' +
  'C2553.202,1209.094 2697.276,1065.02 2875,1065.02' +
  'C3052.724,1065.02 3196.798,1209.094 3196.798,1386.818Z';

/**
 * The mark as GEOMETRY, for renderers that are not the DOM.
 *
 * The PDF export draws this same outline with jsPDF's `moveTo`/`curveTo`/`fill`, which means the
 * exported document carries the real vector mark rather than a screenshot of one. Exposed from
 * here rather than copied into each one, because `MARK` above says "REGENERATE, do not
 * hand-edit" and a second copy is exactly how the two drift.
 *
 * Costs nothing in the browser's bundle: `MARK` is already in the eager chunk via `Logo`.
 */
export const LOGO_GEOMETRY = {
  path: MARK,
  /** The ink box inside the export's oversized canvas — see `INK`. */
  x: INK.x,
  y: INK.y,
  width: INK.width,
  height: INK.height,
  /** Width ÷ height. The mark is taller than it is wide. */
  aspect: ASPECT,
} as const;

